// import 'virtual:objectiv-developer-tools';
import {
  InsifyObjectivProvider,
  getDeviceIdentityContext,
  getDistributionIdentityContext,
  getUserIdentityContext,
  importDevtoolsDynamically,
} from '@insify/objectiv';
import { IdentityContextPlugin } from '@objectiv/plugin-identity-context';
import { LocaleContextPlugin } from '@objectiv/plugin-locale-context';
import { RootLocationContextFromURLPlugin } from '@objectiv/plugin-root-location-context-from-url';
import { makeId } from '@objectiv/tracker-core';
import { useTranslation } from 'react-i18next';

import { useProfile } from '../../api/queries/useProfile';
import { env, isProd } from '../../env';
import { Analytics } from '../../utils/analytics';
import { getAppDomain } from '../../utils/localize';

const applicationId = `${env}-myinsify`;

// Importing virtual:objectiv-developer-tools works on build time while importDevtoolsDynamically works on runtime.
// Importing the dev tools on runtime misses some logs during the initialization which is fine in normal usage.
// If you need to debug the initialization process, you can uncomment the "import 'virtual:objectiv-developer-tools';" on top and comment out the following lines to avoid a duplicated import.
if (window.insify_runtime_config.OBJECTIV_DEVTOOLS === 'true') {
  importDevtoolsDynamically();
}

export const ObjectivTrackerProvider: React.FC<{ children: React.ReactNode; isConsentGiven: boolean }> = ({
  children,
  isConsentGiven,
}) => {
  const { i18n } = useTranslation();

  const { data: profile } = useProfile();
  const deviceId = Analytics.getDeviceId();

  const identityContext = [
    ...getUserIdentityContext(profile?.result?.userId),
    ...getDistributionIdentityContext(),
    ...getDeviceIdentityContext(deviceId),
  ];

  const detectedCountry = getAppDomain();

  const plugins = [
    new LocaleContextPlugin({
      idFactoryFunction: () => i18n.language,
      languageFactoryFunction: () => i18n.language,
      countryFactoryFunction: () => detectedCountry.toUpperCase(),
    }),
    new RootLocationContextFromURLPlugin({
      idFactoryFunction: () => {
        const paths = location.pathname.split('/');
        const lastSlug = paths.pop();

        if (paths.length === 2 && paths[1] === 'products') {
          return makeId('product-detail');
        }

        return lastSlug ? makeId(lastSlug) : 'home';
      },
    }),
    new IdentityContextPlugin(identityContext),
  ];

  return (
    <InsifyObjectivProvider
      applicationId={applicationId}
      isProd={isProd}
      active={true}
      isConsentGiven={isConsentGiven}
      plugins={plugins}
      isLogEnabled={window.insify_runtime_config.OBJECTIV_DEVTOOLS === 'true'}
    >
      {children}
    </InsifyObjectivProvider>
  );
};
